/**
 * Fortis Elements payment widget.
 *
 * Loads the Commerce.js SDK, creates a ticket intention via the
 * caller-supplied `createIntention` function, and mounts the
 * Elements card-entry form. On successful tokenization, calls
 * `onTicketReady` with the ticket ID that the backend will exchange
 * for a permanent token.
 *
 * `createIntention` defaults to the storefront checkout — BioMatch
 * checkout supplies its own. Tickets are fungible client-side; the
 * BE that finalizes the charge decides which payment context owns it.
 */
import React, {useEffect, useRef, useState} from 'react';
import {CheckoutService} from '../services/checkoutService';

interface FortisPaymentWidgetProps {
    /** Called with the ticket ID when the customer completes card entry. */
    onTicketReady: (ticketId: string) => void;
    /** Called when payment fails or is declined. */
    onError: (message: string) => void;
    /** Whether the widget should be interactive. */
    disabled?: boolean;
    /** Override the ticket-creation source. Defaults to shop checkout. */
    createIntention?: () => Promise<{clientToken: string}>;
    /** DOM id of the Elements mount target. Default keeps storefront stable. */
    containerId?: string;
    scriptUrl?: string;
}

interface CommercePayload {
    '@action'?: string;
    id?: string;
    status_code?: number;
    verbiage?: string;
}

type CommerceResult = CommercePayload & {data?: CommercePayload};

interface CommerceElementsInstance {
    create(options: {container: string; theme: string; showReceipt: boolean}): void;
    on(event: string, handler: (result: CommerceResult) => void): void;
    destroy?: () => void;
    unmount?: () => void;
}

interface CommerceSdk {
    elements: new (clientToken: string) => CommerceElementsInstance;
}

type CommerceWindow = Window & {Commerce?: CommerceSdk};

/** Load Commerce.js SDK once. */
function useCommerceScript(scriptUrl: string) {
    const [ready, setReady] = useState(false);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        setReady(false);
        setFailed(false);
        const scriptId = 'fortis-commercejs';
        const existing = document.getElementById(scriptId) as HTMLScriptElement | null;
        if (existing) {
            if ((window as CommerceWindow).Commerce) {
                setReady(true);
                return;
            }
            const handleLoad = () => setReady(true);
            const handleError = () => setFailed(true);
            existing.addEventListener('load', handleLoad);
            existing.addEventListener('error', handleError);
            return () => {
                existing.removeEventListener('load', handleLoad);
                existing.removeEventListener('error', handleError);
            };
        }

        const script = document.createElement('script');
        script.id = scriptId;
        script.src = scriptUrl;
        script.async = true;
        script.onload = () => setReady(true);
        script.onerror = () => setFailed(true);
        document.head.appendChild(script);
    }, [scriptUrl]);

    return {ready, failed};
}

const FortisPaymentWidget: React.FC<FortisPaymentWidgetProps> = ({
    onTicketReady,
    onError,
    disabled,
    createIntention,
    containerId = 'shop-fortis-elements',
    scriptUrl = 'https://js.sandbox.fortis.tech/commercejs-v1.0.0.min.js',
}) => {
    const intentionFn = createIntention ?? (() => CheckoutService.createIntention());
    const {ready: commerceReady, failed: commerceFailed} = useCommerceScript(scriptUrl);
    const containerRef = useRef<HTMLDivElement>(null);
    const elementsRef = useRef<{cleanup: () => void} | null>(null);
    const [loading, setLoading] = useState(true);
    const [formLoaded, setFormLoaded] = useState(false);
    const submittedRef = useRef(false);

    useEffect(() => {
        if (commerceFailed) onError('Payment system not available. Please refresh and try again.');
    }, [commerceFailed, onError]);

    useEffect(() => {
        if (!commerceReady || !containerRef.current || disabled) return;

        let cancelled = false;
        submittedRef.current = false;

        async function init() {
            try {
                const {clientToken} = await intentionFn();
                if (cancelled) return;

                const Commerce = (window as CommerceWindow).Commerce;
                if (!Commerce) {
                    onError('Payment system not available. Please refresh and try again.');
                    return;
                }

                // Clean up previous instance
                if (elementsRef.current?.cleanup) elementsRef.current.cleanup();
                if (containerRef.current) containerRef.current.innerHTML = '';

                const elements = new Commerce.elements(clientToken);

                elements.create({
                    container: `#${containerId}`,
                    theme: 'default',
                    showReceipt: false,
                });

                const handleResult = (result: CommerceResult) => {
                    const d = result?.data || result;
                    const action = d?.['@action'] || result?.['@action'];
                    const ticketId = d?.id || result?.id;

                    if (action === 'ticket' && ticketId) {
                        if (submittedRef.current) return;
                        submittedRef.current = true;
                        onTicketReady(ticketId);
                        return;
                    }

                    // Check for declined/error
                    const statusCode = d?.status_code ?? result?.status_code;
                    if (statusCode && statusCode >= 200) {
                        const verbiage = d?.verbiage || result?.verbiage || `status ${statusCode}`;
                        onError(`Payment declined: ${verbiage}`);
                    }
                };

                elements.on('paymentFinished', handleResult);
                elements.on('done', handleResult);
                elements.on('success', handleResult);
                elements.on('tokenCreated', handleResult);

                elements.on('ready', () => {
                    setFormLoaded(true);
                    setLoading(false);
                });

                // Store cleanup function
                elementsRef.current = {
                    cleanup: () => {
                        if (typeof elements.destroy === 'function') elements.destroy();
                        else if (typeof elements.unmount === 'function') elements.unmount();
                    },
                };
            } catch {
                if (!cancelled) {
                    onError('Unable to initialize payment form. Please try again.');
                    setLoading(false);
                }
            }
        }

        init();

        return () => {
            cancelled = true;
            if (elementsRef.current?.cleanup) elementsRef.current.cleanup();
        };
    }, [commerceReady, disabled]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div>
            {loading && !formLoaded && (
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mld-primary" />
                    <span className="ml-3 text-sm text-gray-500">Loading payment form...</span>
                </div>
            )}
            <div id={containerId} ref={containerRef} className={loading && !formLoaded ? 'hidden' : ''} />
        </div>
    );
};

export default FortisPaymentWidget;
